/** @format */

import { useCallback, useEffect, useRef, useState } from "react";
import { API_ENDPOINTS } from "../../../constants/api";
import {
  TAzioneTimebank,
  TPresenzaVista,
} from "../../../redux-modules/declarations/maps/presenzeVista";
import { TRequestCreateRequest } from "../../../redux-modules/declarations/maps/requests";
import apiClient from "../../../utils/apiClient";
import { showNotification } from "../../../utils/showNotification";
import { messaggioErrore } from "../ViewTurniSettimana/useTurniSettimana";
import { ESITI_TIMEBANK } from "./format";

export { messaggioErrore };

/** Una lettura ogni minuto quando si guarda oggi, e solo con la scheda del browser visibile. */
export const INTERVALLO_AGGIORNAMENTO = 60000;

export interface TLettura<T> {
  dati: T | null;
  caricamento: boolean;
  aggiornamento: boolean;
  errore: string | null;
  /** rilegge subito se la lettura è attiva, altrimenti alla prossima attivazione */
  ricarica: () => void;
}

/**
 * Lettura di una schermata (GET /presenze/...), con lo stesso schema dei Turni:
 *  - una risposta superata da un cambio di parametri viene scartata;
 *  - con gli stessi parametri la rilettura è silenziosa e i dati restano a video;
 *  - una lettura non attiva (scheda non mostrata) non chiama il backend, e rilegge quando
 *    torna attiva se nel frattempo qualcosa è cambiato.
 */
export const useLettura = <T>(
  url: string,
  parametri: Record<string, string> | null,
  attiva: boolean,
  aggiornaOgniMinuto: boolean,
  ripiegoErrore: string,
  /**
   * Parametri effettivi, calcolati al momento di ogni chiamata (per esempio l'ora corrente).
   * I `parametri` restano la chiave della lettura: se cambiano è una lettura nuova.
   */
  alMomento?: (parametri: Record<string, string>) => Record<string, string>,
): TLettura<T> => {
  const [dati, setDati] = useState<T | null>(null);
  const [caricamento, setCaricamento] = useState(false);
  const [aggiornamento, setAggiornamento] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const richiesta = useRef(0);
  const letta = useRef<string | null>(null);
  const sporca = useRef(false);
  const calcola = useRef(alMomento);
  calcola.current = alMomento;

  const chiave = parametri ? JSON.stringify(parametri) : null;

  const leggi = useCallback((): void => {
    if (!chiave) return;
    const numero = ++richiesta.current;
    const stessa = letta.current === chiave;
    sporca.current = false;
    if (stessa) setAggiornamento(true);
    else setCaricamento(true);

    apiClient
      .get(url, {
        params: calcola.current ? calcola.current(JSON.parse(chiave)) : JSON.parse(chiave),
      })
      .then(res => {
        if (numero !== richiesta.current) return;
        setDati(res.data);
        letta.current = chiave;
        setErrore(null);
      })
      .catch(err => {
        if (numero !== richiesta.current) return;
        setErrore(messaggioErrore(err, ripiegoErrore));
      })
      .finally(() => {
        if (numero !== richiesta.current) return;
        setCaricamento(false);
        setAggiornamento(false);
      });
  }, [url, chiave, ripiegoErrore]);

  // nuova lettura quando cambiano i parametri, o quando torna attiva con dati superati
  useEffect(() => {
    if (!attiva || !chiave) return;
    if (letta.current !== chiave || sporca.current) leggi();
  }, [attiva, chiave, leggi]);

  useEffect(() => {
    if (!attiva || !aggiornaOgniMinuto) return undefined;
    const timer = window.setInterval(() => {
      if (!document.hidden) leggi();
    }, INTERVALLO_AGGIORNAMENTO);
    return (): void => window.clearInterval(timer);
  }, [attiva, aggiornaOgniMinuto, leggi]);

  const ricarica = useCallback((): void => {
    if (attiva) leggi();
    else sporca.current = true;
  }, [attiva, leggi]);

  return {
    dati: chiave ? dati : null,
    caricamento,
    aggiornamento,
    errore: chiave ? errore : null,
    ricarica,
  };
};

// ------------------------------------------------------------------ azioni

export interface TEsitoAzione {
  ok: boolean;
  messaggio: string;
}

const conEsito = (chiamata: Promise<unknown>, ok: string, ripiego: string): Promise<TEsitoAzione> =>
  chiamata
    .then(() => ({ ok: true, messaggio: ok }))
    .catch(err => ({ ok: false, messaggio: messaggioErrore(err, ripiego) }));

const formatoBackend = (dataOra: string): string =>
  dataOra.length === 16 ? `${dataOra}:00` : dataOra;

/**
 * Chiamata esistente per ogni azione su una timbratura da approvare (contratto del passo 1):
 *  - approva / approva_straordinario: POST presences_timebank_approve
 *  - rifiuta / rifiuta_straordinario: GET timebank_diniego/{timebank_id}
 *  - crea_rol / conferma_turno_parziale: approvazione con negative_straordinario
 */
export const chiamataTimebank = (
  presenza: TPresenzaVista,
  codice: TAzioneTimebank,
  nascondiStorico = false,
): Promise<unknown> => {
  const approva = (corpo: Record<string, boolean>): Promise<unknown> =>
    apiClient.post(API_ENDPOINTS.PRESENCES + "_timebank_approve", {
      presence_id: presenza.id,
      is_straordinario: false,
      negative_straordinario: false,
      conferma_turno_parziale: false,
      hide_history: nascondiStorico,
      ...corpo,
    });
  const rifiuta = (straordinario: boolean): Promise<unknown> =>
    apiClient.get(`${API_ENDPOINTS.TIMEBANK_DINIEGO}/${presenza.timebank?.id}`, {
      params: { is_straordinario: straordinario ? 1 : 0, hide_history: nascondiStorico ? 1 : 0 },
    });
  const straordinario = presenza.tipo.codice_sistema === "STRAORDINARIO";

  switch (codice) {
    case "approva":
      return approva({});
    case "rifiuta":
      return rifiuta(false);
    case "approva_straordinario":
      // presenza già di straordinario: si conferma come è (comportamento della vista precedente)
      return approva({ is_straordinario: true, conferma_turno_parziale: straordinario });
    case "rifiuta_straordinario":
      return rifiuta(true);
    case "crea_rol":
      return approva({ is_straordinario: true, negative_straordinario: true });
    case "conferma_turno_parziale":
      return approva({
        is_straordinario: true,
        negative_straordinario: true,
        conferma_turno_parziale: true,
      });
    default:
      return Promise.reject(new Error("Azione non prevista"));
  }
};

export const STATO_RICHIESTA = { accettata: 1, rifiutata: 4, annullata: 5 };

export interface TAzioniPresenze {
  timebank: (
    presenza: TPresenzaVista,
    codice: TAzioneTimebank,
    nascondiStorico?: boolean,
  ) => Promise<TEsitoAzione>;
  correggiOrari: (
    presenza: TPresenzaVista,
    modo: "timebank" | "presenza",
    entrata: string,
    uscita: string,
    giorno: string,
  ) => Promise<TEsitoAzione>;
  inserisciPresenza: (
    userId: number,
    entrata: string,
    uscita: string,
    tipoTurnoId: number,
    tipoPresenzaId: number,
  ) => Promise<TEsitoAzione>;
  assegnaTurno: (
    presenza: TPresenzaVista,
    tipoTurnoId: string,
    giorno: string,
  ) => Promise<TEsitoAzione>;
  cambiaTurno: (
    presenza: TPresenzaVista,
    tipoTurnoId: string,
    giorno: string,
  ) => Promise<TEsitoAzione>;
  eliminaPresenza: (presenza: TPresenzaVista) => Promise<TEsitoAzione>;
  statoRichiesta: (richiestaId: number, userId: number, stato: number) => Promise<TEsitoAzione>;
  nuovaRichiesta: (richiesta: TRequestCreateRequest) => Promise<TEsitoAzione>;
}

/** Le chiamate esistenti, senza notifiche: chi le usa decide come mostrare l'esito. */
export const azioniPresenze: TAzioniPresenze = {
  timebank: (presenza, codice, nascondiStorico = false) =>
    conEsito(
      chiamataTimebank(presenza, codice, nascondiStorico),
      ESITI_TIMEBANK[codice],
      "Operazione non riuscita",
    ),

  correggiOrari: (presenza, modo, entrata, uscita, giorno) =>
    conEsito(
      modo === "timebank"
        ? apiClient.post(API_ENDPOINTS.PRESENCES + "_timebank_update", {
          presence_id: presenza.id,
          start: formatoBackend(entrata),
          stop: formatoBackend(uscita),
          current_date: giorno,
        })
        : apiClient.put(`${API_ENDPOINTS.PRESENCES}/${presenza.id}`, {
          presence_id: presenza.id,
          datetime_from: formatoBackend(entrata),
          datetime_to: formatoBackend(uscita),
        }),
      "Orari corretti",
      "Orari non corretti",
    ),

  inserisciPresenza: (userId, entrata, uscita, tipoTurnoId, tipoPresenzaId) =>
    conEsito(
      apiClient.post(API_ENDPOINTS.PRESENCES + "/create_artificial", {
        user_id: userId,
        datetime_from: formatoBackend(entrata),
        datetime_to: formatoBackend(uscita),
        shift_type_id: tipoTurnoId,
        presence_type_id: tipoPresenzaId,
      }),
      "Presenza inserita",
      "Presenza non inserita",
    ),

  assegnaTurno: (presenza, tipoTurnoId, giorno) =>
    conEsito(
      apiClient.post(API_ENDPOINTS.PRESENCES + "_planning_assign", {
        presence_id: presenza.id,
        shift_type_id: tipoTurnoId,
        current_date: giorno,
      }),
      "Turno assegnato",
      "Turno non assegnato",
    ),

  cambiaTurno: (presenza, tipoTurnoId, giorno) =>
    conEsito(
      apiClient.post(API_ENDPOINTS.PRESENCES + "_planning_switch", {
        presence_id: presenza.id,
        shift_type_id: tipoTurnoId,
        current_date: giorno,
      }),
      "Turno cambiato",
      "Turno non cambiato",
    ),

  eliminaPresenza: presenza =>
    conEsito(
      apiClient.delete(`${API_ENDPOINTS.PRESENCES}/${presenza.id}`),
      "Presenza eliminata",
      "Presenza non eliminata",
    ),

  statoRichiesta: (richiestaId, userId, stato) =>
    conEsito(
      apiClient.post(API_ENDPOINTS.WORKLOW_STATUSES, {
        request_id: richiestaId,
        justificative_status_id: stato,
        user_id: userId,
      }),
      stato === STATO_RICHIESTA.accettata
        ? "Richiesta accettata"
        : stato === STATO_RICHIESTA.rifiutata
          ? "Richiesta rifiutata"
          : "Richiesta annullata",
      "Stato della richiesta non aggiornato",
    ),

  nuovaRichiesta: richiesta =>
    conEsito(
      apiClient.post(API_ENDPOINTS.REQUESTS, richiesta),
      "Richiesta inserita",
      "Richiesta non inserita",
    ),
};

/** Mostra l'esito in una notifica; restituisce se è andata a buon fine. */
export const notificaEsito = (
  esito: TEsitoAzione,
  titoloErrore = "Operazione non riuscita",
): boolean => {
  if (esito.ok) showNotification("success", esito.messaggio, "");
  else showNotification("error", titoloErrore, esito.messaggio);
  return esito.ok;
};
